import { createSlice } from "@reduxjs/toolkit";

// Initial state with flights, accommodations, and daily plans
const initialState = {
  flights: {},
  accommodations: {},
  dailyPlans: {}, // Added to store daily plans
  currentFlightId: null,
  currentTripId: null,
  currentTrip: null,
  currentAccommodationId: null,
  currentDailyPlanId: null, // To store selected daily plan ID
};

// Create a Redux slice for flights, accommodations, and daily plans
const tripsSlice = createSlice({
  name: "trips", // Name of the slice
  initialState, // The initial state defined above
  reducers: {
    // Set all flights
    setFlights(state, action) {
      // Convert the array of flights to an object keyed by flight_id
      state.flights = action.payload.reduce((acc, flight) => {
        acc[flight.flight_id] = flight;
        return acc;
      }, {});
    },
    // Set all accommodations
    setAccommodations(state, action) {
      // Convert the array of accommodations to an object keyed by accommodation_id
      state.accommodations = action.payload.reduce((acc, accommodation) => {
        acc[accommodation.accommodation_id] = accommodation;
        return acc;
      }, {});
    },
    // Set all daily plans
    setDailyPlans(state, action) {
      // Convert the array of daily plans to an object keyed by daily_plan_id
      state.dailyPlans = action.payload.reduce((acc, dailyPlan) => {
        acc[dailyPlan.daily_plan_id] = dailyPlan;
        return acc;
      }, {});
    },
    // Set the currently selected trip
    setCurrentTrip(state, action) {
      state.currentTrip = action.payload;
    },
    // Set the currently selected trip ID
    setCurrentTripId(state, action) {
      state.currentTripId = action.payload;
    },
    // Reducer to update or add a single flight
    updateFlight(state, action) {
      const updatedFlight = action.payload;
      // Update the flight in the state using flight_id as the key
      state.flights[updatedFlight.flight_id] = updatedFlight;
    },
    // Reducer to update or add a single accommodation
    updateAccommodation(state, action) {
      const updatedAccommodation = action.payload;
      // Update the accommodation in the state using accommodation_id as the key
      state.accommodations[updatedAccommodation.accommodation_id] =
        updatedAccommodation;
    },
    // Reducer to update or add a single daily plan
    updateDailyPlan(state, action) {
      const updatedDailyPlan = action.payload;
      // Update the daily plan in the state using daily_plan_id as the key
      state.dailyPlans[updatedDailyPlan.daily_plan_id] = updatedDailyPlan;
    },
    // Set the currently selected flight ID
    setCurrentFlightId(state, action) {
      state.currentFlightId = action.payload;
    },
    // Set the currently selected accommodation ID
    setCurrentAccommodationId(state, action) {
      state.currentAccommodationId = action.payload;
    },
    // Set the currently selected daily plan ID
    setCurrentDailyPlanId(state, action) {
      state.currentDailyPlanId = action.payload;
    },
  },
});

// Export action creators for use in components
export const {
  setFlights,
  setAccommodations,
  setDailyPlans, // Added action to set daily plans
  updateFlight,
  updateAccommodation,
  updateDailyPlan, // Added action to update daily plans
  setCurrentTrip,
  setCurrentAccommodationId,
  setCurrentTripId,
  setCurrentFlightId,
  setCurrentDailyPlanId, // Added action to set the current daily plan ID
} = tripsSlice.actions;

// Selector to get current flight details
export const selectCurrentFlight = (state) => {
  const { currentFlightId, flights } = state.trips;
  return flights[currentFlightId] || null;
};

// Selector to get current trip details
export const selectCurrentTrip = (state) => {
  return state.trips.currentTrip || null;
};

// Selector to get current accommodation details
export const selectCurrentAccommodation = (state) => {
  const { currentAccommodationId, accommodations } = state.trips;
  return accommodations[currentAccommodationId] || null;
};

// Selector to get current daily plan details
export const selectCurrentDailyPlan = (state) => {
  const { currentDailyPlanId, dailyPlans } = state.trips;
  return dailyPlans[currentDailyPlanId] || null;
};

// Export reducer to be added to the store
export const tripsReducer = tripsSlice.reducer;
